<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ConnectionStatus from './ConnectionStatus.vue'
import ResourceBar from './ResourceBar.vue'
import UiIcon from './UiIcon.vue'
import VillageSwitcher from './VillageSwitcher.vue'
import { request } from '../core/api'
import { formatCoordinate } from '../core/coordinates'
import { worldCatalog } from '../core/catalog'
import { preferences } from '../core/preferences'
import { formatNumber } from '../core/i18n'
import {
  minimumVillageRevision,
  observeVillageRevision,
  subscribeVillage,
} from '../core/liveSync'
import { navigate, router } from '../core/router'
import { adminEntryPath, clearSession, session } from '../core/session'
import type {
  NavDisplayMode,
  VillageOverview,
  VillageResource,
  VillageResources,
} from '../core/types'
import { preferredVillage, villagePreferenceKey } from '../core/villages'
import { uiText } from '../core/ui'

interface NavItem {
  key: string
  label: string
  icon: string
  path: string
  group: string
  capability?: string
}

interface TopNavItem {
  key: string
  label: string
  icon: string
  path: string
}

const world = computed(
  () =>
    session.bootstrap?.worlds.find(
      (item) => item.worldId === router.current.worldId,
    ) ?? null,
)
const adminWorkspace = computed(() => router.current.name.startsWith('admin-'))
const adminPath = computed(() => adminEntryPath())
watch(
  () => world.value?.worldId ?? '',
  (worldId) => {
    if (worldId) void worldCatalog(worldId)
  },
  { immediate: true },
)
const villageId = computed(() => router.current.villageId)
const defaultVillage = computed(() => {
  if (!world.value) return null
  if (villageId.value)
    return (
      world.value.villages.find((v) => v.villageId === villageId.value) ?? null
    )
  return preferredVillage(
    world.value.villages,
    localStorage.getItem(
      villagePreferenceKey(
        session.account?.accountId ?? 'anonymous',
        world.value.worldId,
      ),
    ),
  )
})
const villageBase = computed(() =>
  world.value && defaultVillage.value
    ? `/world/${world.value.worldId}/village/${defaultVillage.value.villageId}`
    : null,
)
const nav = computed<NavItem[]>(() => {
  if (!world.value) return []
  const w = `/world/${world.value.worldId}`
  const v = villageBase.value
  const items: NavItem[] = [
    {
      key: 'world-dashboard',
      label: uiText('Reichsübersicht', 'Empire overview'),
      icon: 'dashboard',
      path: `${w}/dashboard`,
      group: uiText('Reich', 'Empire'),
      capability: 'dashboard',
    },
  ]
  if (v) {
    items.push(
      {
        key: 'village',
        label: uiText('Dorfübersicht', 'Village overview'),
        icon: 'village',
        path: `${v}/village`,
        group: uiText('Dorf', 'Village'),
        capability: 'dashboard',
      },
      {
        key: 'buildings',
        label: uiText('Gebäude', 'Buildings'),
        icon: 'buildings',
        path: `${v}/buildings`,
        group: uiText('Dorf', 'Village'),
        capability: 'buildings',
      },
      {
        key: 'construction',
        label: uiText('Bauaufträge', 'Construction'),
        icon: 'construction',
        path: `${v}/construction`,
        group: uiText('Dorf', 'Village'),
        capability: 'buildings',
      },
      {
        key: 'army',
        label: uiText('Armee', 'Army'),
        icon: 'army',
        path: `${v}/army`,
        group: uiText('Militär', 'Military'),
        capability: 'army',
      },
      {
        key: 'training',
        label: uiText('Rekrutierung', 'Recruitment'),
        icon: 'training',
        path: `${v}/training`,
        group: uiText('Militär', 'Military'),
        capability: 'training',
      },
      {
        key: 'attack',
        label: uiText('Angriffe', 'Attacks'),
        icon: 'attack',
        path: `${v}/attack`,
        group: uiText('Militär', 'Military'),
        capability: 'movement',
      },
      {
        key: 'support',
        label: uiText('Unterstützung', 'Support'),
        icon: 'support',
        path: `${v}/support`,
        group: uiText('Militär', 'Military'),
        capability: 'support',
      },
      {
        key: 'espionage',
        label: uiText('Spionage', 'Espionage'),
        icon: 'espionage',
        path: `${v}/espionage`,
        group: uiText('Militär', 'Military'),
        capability: 'espionage',
      },
      {
        key: 'research',
        label: uiText('Forschung', 'Research'),
        icon: 'research',
        path: `${v}/research`,
        group: uiText('Entwicklung', 'Development'),
        capability: 'research',
      },
      {
        key: 'smithy',
        label: uiText('Schmiede', 'Smithy'),
        icon: 'smithy',
        path: `${v}/smithy`,
        group: uiText('Entwicklung', 'Development'),
        capability: 'smithy',
      },
      {
        key: 'nobility',
        label: uiText('Adel', 'Nobility'),
        icon: 'nobility',
        path: `${v}/nobility`,
        group: uiText('Entwicklung', 'Development'),
        capability: 'nobility',
      },
      {
        key: 'market',
        label: uiText('Markt', 'Market'),
        icon: 'market',
        path: `${v}/market`,
        group: uiText('Wirtschaft', 'Economy'),
        capability: 'market',
      },
    )
  }
  items.push(
    {
      key: 'map',
      label: uiText('Weltkarte', 'World map'),
      icon: 'map',
      path: `${w}/map`,
      group: uiText('Karte', 'Map'),
      capability: 'map',
    },
    {
      key: 'movements',
      label: uiText('Bewegungen', 'Movements'),
      icon: 'movements',
      path: `${w}/movements`,
      group: uiText('Militär', 'Military'),
      capability: 'movement',
    },
    {
      key: 'reports',
      label: uiText('Berichte', 'Reports'),
      icon: 'reports',
      path: `${w}/reports`,
      group: uiText('Auswertung', 'Reports'),
    },
    {
      key: 'alliance',
      label: uiText('Allianz', 'Alliance'),
      icon: 'alliance',
      path: `${w}/alliance`,
      group: uiText('Allianz', 'Alliance'),
      capability: 'alliance',
    },
    {
      key: 'quests',
      label: uiText('Quests & Events', 'Quests & events'),
      icon: 'quests',
      path: `${w}/quests`,
      group: uiText('Welt', 'World'),
      capability: 'content',
    },
    {
      key: 'inventory',
      label: uiText('Inventar', 'Inventory'),
      icon: 'inventory',
      path: `${w}/inventory`,
      group: uiText('Welt', 'World'),
      capability: 'inventory',
    },
    {
      key: 'rankings',
      label: uiText('Ranglisten', 'Rankings'),
      icon: 'rankings',
      path: `${w}/rankings`,
      group: uiText('Welt', 'World'),
    },
    {
      key: 'victory',
      label: uiText('Weltstatus', 'World status'),
      icon: 'victory',
      path: `${w}/victory`,
      group: uiText('Welt', 'World'),
      capability: 'endgame',
    },
    {
      key: 'messages',
      label: uiText('Nachrichten', 'Messages'),
      icon: 'messages',
      path: `${w}/messages`,
      group: uiText('Kommunikation', 'Communication'),
      capability: 'messages',
    },
    {
      key: 'chat',
      label: 'Chat',
      icon: 'chat',
      path: `${w}/chat`,
      group: uiText('Kommunikation', 'Communication'),
      capability: 'chat',
    },
    {
      key: 'social',
      label: uiText('Freunde & Kontakte', 'Friends & contacts'),
      icon: 'friends',
      path: `${w}/social`,
      group: uiText('Kommunikation', 'Communication'),
    },
    {
      key: 'advanced',
      label: uiText('Weitere Funktionen', 'More features'),
      icon: 'more',
      path: `${w}/advanced`,
      group: uiText('Sonstiges', 'Other'),
    },
  )
  return items.filter(
    (item) =>
      !item.capability || world.value?.capabilities[item.capability] === true,
  )
})

const groupOrder = computed(() => [
  uiText('Reich', 'Empire'),
  uiText('Dorf', 'Village'),
  uiText('Militär', 'Military'),
  uiText('Entwicklung', 'Development'),
  uiText('Wirtschaft', 'Economy'),
  uiText('Allianz', 'Alliance'),
  uiText('Kommunikation', 'Communication'),
  uiText('Welt', 'World'),
  uiText('Karte', 'Map'),
  uiText('Auswertung', 'Reports'),
  uiText('Sonstiges', 'Other'),
])
const groups = computed(() =>
  groupOrder.value
    .map((name) => ({
      name,
      items: nav.value.filter((item) => item.group === name),
    }))
    .filter((group) => group.items.length > 0),
)

function navItem(key: string): NavItem | null {
  return nav.value.find((item) => item.key === key) ?? null
}

const topNavigation = computed<TopNavItem[]>(() => {
  const definitions: Array<[string, string, string]> = [
    ['village', uiText('Dorf', 'Village'), 'village'],
    ['map', uiText('Karte', 'Map'), 'map'],
    ['army', uiText('Armee', 'Army'), 'army'],
    ['reports', uiText('Berichte', 'Reports'), 'reports'],
    ['messages', uiText('Nachrichten', 'Messages'), 'messages'],
    ['alliance', uiText('Allianz', 'Alliance'), 'alliance'],
    ['rankings', uiText('Ranglisten', 'Rankings'), 'rankings'],
    ['quests', uiText('Welt', 'World'), 'quests'],
  ]
  return definitions.flatMap(([key, label, icon]) => {
    const item = navItem(key)
    return item ? [{ key, label, icon, path: item.path }] : []
  })
})

const topSectionKey = computed(() => {
  const name = router.current.name
  if (name === 'world-dashboard') return 'quests'
  if (
    [
      'village',
      'buildings',
      'building',
      'construction',
      'training',
      'research',
      'smithy',
      'market',
      'nobility',
    ].includes(name)
  )
    return 'village'
  if (['army', 'movements', 'attack', 'support', 'espionage'].includes(name))
    return 'army'
  if (name === 'map') return 'map'
  if (name === 'reports') return 'reports'
  if (['messages', 'chat', 'social', 'notifications'].includes(name))
    return 'messages'
  if (['alliance', 'alliance-forum', 'forum-topic'].includes(name))
    return 'alliance'
  if (name === 'rankings') return 'rankings'
  if (['quests', 'inventory', 'victory', 'advanced'].includes(name))
    return 'quests'
  return ''
})

type ExpandedSidebarMode = Exclude<NavDisplayMode, 'collapsed' | 'icons-only'>

const sidebarSection = computed(() => {
  const definitions: Partial<
    Record<string, { label: string; keys: string[] }>
  > = {
    village: {
      label: uiText('Dorf', 'Village'),
      keys: [
        'village',
        'buildings',
        'construction',
        'training',
        'research',
        'smithy',
        'nobility',
        'market',
      ],
    },
    army: {
      label: uiText('Armee', 'Army'),
      keys: ['army', 'attack', 'support', 'espionage', 'movements'],
    },
    map: {
      label: uiText('Karte', 'Map'),
      keys: ['map'],
    },
    reports: {
      label: uiText('Berichte', 'Reports'),
      keys: ['reports'],
    },
    messages: {
      label: uiText('Nachrichten', 'Messages'),
      keys: ['messages', 'chat', 'social'],
    },
    alliance: {
      label: uiText('Allianz', 'Alliance'),
      keys: ['alliance'],
    },
    rankings: {
      label: uiText('Ranglisten', 'Rankings'),
      keys: ['rankings'],
    },
    quests: {
      label: uiText('Welt', 'World'),
      keys: ['world-dashboard', 'quests', 'inventory', 'victory', 'advanced'],
    },
  }
  const definition = definitions[topSectionKey.value] ??
    definitions.quests ?? { label: uiText('Welt', 'World'), keys: [] }
  return {
    label: definition.label,
    items: definition.keys.flatMap((key) => {
      const item = navItem(key)
      return item ? [item] : []
    }),
  }
})

const sidebarContextName = computed(() => {
  if (defaultVillage.value && ['village', 'army'].includes(topSectionKey.value))
    return defaultVillage.value.displayName
  return world.value?.displayName ?? world.value?.worldId ?? ''
})

const lastExpandedSidebarMode = ref<ExpandedSidebarMode>(
  preferences.navDisplay === 'text-only' ? 'text-only' : 'icons-text',
)
watch(
  () => preferences.navDisplay,
  (mode) => {
    if (mode === 'icons-text' || mode === 'text-only')
      lastExpandedSidebarMode.value = mode
  },
)
const sidebarCompact = computed(() => preferences.navDisplay === 'icons-only')
function toggleSidebarDensity(): void {
  if (preferences.navDisplay === 'icons-only') {
    preferences.navDisplay = lastExpandedSidebarMode.value
    return
  }
  if (
    preferences.navDisplay === 'icons-text' ||
    preferences.navDisplay === 'text-only'
  )
    lastExpandedSidebarMode.value = preferences.navDisplay
  preferences.navDisplay = 'icons-only'
}

const primaryMobile = computed(() =>
  nav.value
    .filter((item) =>
      ['world-dashboard', 'map', 'army', 'reports'].includes(item.key),
    )
    .slice(0, 4),
)
const secondaryMobile = computed(() =>
  nav.value.filter((item) => !primaryMobile.value.includes(item)),
)

const shellResources = ref<VillageResource[]>([])
const shellResourceRevision = ref(0)
const shellOverview = ref<VillageOverview | null>(null)
let shellLoadGeneration = 0
async function loadShellResources(): Promise<number | null> {
  if (!world.value || !defaultVillage.value) return null
  const generation = ++shellLoadGeneration
  try {
    const worldId = world.value.worldId
    const selectedVillageId = defaultVillage.value.villageId
    const villagePath = `/api/v2/worlds/${worldId}/villages/${selectedVillageId}`
    const [resourceData, overview] = await Promise.all([
      request<VillageResources>(`${villagePath}/resources`),
      request<VillageOverview>(villagePath),
    ])
    if (
      generation !== shellLoadGeneration ||
      world.value?.worldId !== worldId ||
      defaultVillage.value?.villageId !== selectedVillageId
    )
      return null
    shellResources.value = resourceData.resources
    shellResourceRevision.value = resourceData.villageRevision
    shellOverview.value = overview
    observeVillageRevision(worldId, selectedVillageId, resourceData.villageRevision)
    observeVillageRevision(worldId, selectedVillageId, overview.revision)
    return minimumVillageRevision(resourceData.villageRevision, overview.revision)
  } catch {
    // Background reconciliation keeps the last canonical snapshot visible.
    // Context changes clear the old village explicitly in the watcher below.
    return null
  }
}
watch(
  () =>
    `${world.value?.worldId ?? ''}:${defaultVillage.value?.villageId ?? ''}`,
  () => {
    shellLoadGeneration += 1
    shellResources.value = []
    shellResourceRevision.value = 0
    shellOverview.value = null
    void loadShellResources()
  },
  { immediate: true },
)

let stopVillageSync: (() => void) | null = null
watch(
  () =>
    [
      world.value?.worldId ?? '',
      defaultVillage.value?.villageId ?? '',
    ] as const,
  ([worldId, selectedVillageId]) => {
    stopVillageSync?.()
    stopVillageSync = null
    if (!worldId || !selectedVillageId) return
    stopVillageSync = subscribeVillage(
      worldId,
      selectedVillageId,
      loadShellResources,
    )
  },
  { immediate: true },
)
onBeforeUnmount(() => stopVillageSync?.())

function active(path: string): boolean {
  return (
    router.current.path === path ||
    (path.endsWith('/village') && router.current.name === 'village')
  )
}
async function signOut(): Promise<void> {
  try {
    await request('/api/v2/sessions/current', { method: 'DELETE' })
  } catch {
    // local logout still clears the token
  }
  clearSession()
  navigate('/login', true)
}
</script>

<template>
  <div
    class="app-shell"
    :class="{ 'portal-shell': !world }"
    :data-nav="preferences.navDisplay"
  >
    <a class="skip-link" href="#main-content">{{
      uiText('Zum Inhalt springen', 'Skip to content')
    }}</a>

    <header class="game-frame-header">
      <div class="topbar" :class="{ 'portal-topbar': !world }">
        <a href="/worlds" data-router-link class="brand">
          <span class="brand-mark" aria-hidden="true"
            ><img src="/assets/craft-imperium/brand/crest.svg" alt=""
          /></span>
          <span class="brand-copy">
            <strong>CRAFT</strong>
            <strong>IMPERIUM</strong>
          </span>
        </a>

        <nav
          v-if="!world"
          class="portal-context-nav"
          :aria-label="uiText('Portalnavigation', 'Portal navigation')"
        >
          <a
            href="/worlds"
            data-router-link
            :aria-current="
              router.current.name === 'worlds' ? 'page' : undefined
            "
          >
            <UiIcon name="worlds" />
            <span>{{ uiText('Welten', 'Worlds') }}</span>
          </a>
          <a
            v-if="adminPath"
            :href="adminPath"
            data-router-link
            :aria-current="adminWorkspace ? 'page' : undefined"
          >
            <UiIcon name="admin" />
            <span>{{ uiText('Administration', 'Administration') }}</span>
          </a>
        </nav>

        <div v-if="world" class="world-context">
          <a href="/worlds" data-router-link class="world-selector">
            <UiIcon name="worlds" />
            <span>{{ world.displayName ?? world.worldId }}</span>
          </a>
          <VillageSwitcher
            :world="world"
            :village-id="defaultVillage?.villageId"
          />
        </div>

        <nav class="top-actions" :aria-label="uiText('Konto', 'Account')">
          <ConnectionStatus />
          <a
            :href="world ? `/world/${world.worldId}/messages` : '/worlds'"
            data-router-link
            class="icon-button"
            :aria-label="uiText('Nachrichten', 'Messages')"
            ><UiIcon name="messages"
          /></a>
          <a
            :href="
              world ? `/world/${world.worldId}/notifications` : '/notifications'
            "
            data-router-link
            class="icon-button"
            :aria-label="uiText('Meldungen', 'Notifications')"
            ><UiIcon name="notifications"
          /></a>
          <a
            href="/account"
            data-router-link
            class="icon-button"
            :aria-label="uiText('Profil', 'Profile')"
            ><UiIcon name="profile"
          /></a>
          <a
            href="/security"
            data-router-link
            class="icon-button"
            :aria-label="uiText('Sicherheitscenter', 'Security center')"
            ><UiIcon name="warning"
          /></a>
          <a
            href="/settings"
            data-router-link
            class="icon-button"
            :aria-label="uiText('Einstellungen', 'Settings')"
            ><UiIcon name="settings"
          /></a>
          <button
            type="button"
            class="icon-button"
            :aria-label="uiText('Abmelden', 'Sign out')"
            @click="signOut"
          >
            <UiIcon name="back" />
          </button>
        </nav>
      </div>

      <div v-if="world" class="resource-ribbon">
        <ResourceBar
          v-if="shellResources.length > 0"
          :resources="shellResources"
          :village-revision="shellResourceRevision"
          :speed-bps="world.speedBps"
        />
        <span v-else class="resource-placeholder">{{
          uiText(
            'Ressourcen des aktiven Dorfs',
            'Resources of the active village',
          )
        }}</span>
        <div v-if="defaultVillage" class="village-ribbon-meta">
          <span v-if="shellOverview"
            ><UiIcon name="population" />
            {{ formatNumber(shellOverview.points) }} P</span
          >
          <span class="coordinates"
            >{{
              formatCoordinate(
                defaultVillage.coordinate.x,
                defaultVillage.coordinate.y,
              )
            }}</span
          >
        </div>
      </div>

      <nav
        v-if="world"
        class="game-nav"
        :aria-label="uiText('Hauptnavigation', 'Main navigation')"
      >
        <a
          v-for="item in topNavigation"
          :key="item.key"
          :href="item.path"
          data-router-link
          :class="{ active: topSectionKey === item.key }"
        >
          <UiIcon :name="item.icon" />
          <span class="nav-label">{{ item.label }}</span>
        </a>
        <details class="all-sections-menu">
          <summary>
            <UiIcon name="more" /><span class="nav-label">{{
              uiText('Mehr', 'More')
            }}</span>
          </summary>
          <div class="all-sections-panel">
            <section v-for="group in groups" :key="group.name">
              <strong>{{ group.name }}</strong>
              <a
                v-for="item in group.items"
                :key="item.path"
                :href="item.path"
                data-router-link
                >{{ item.label }}</a
              >
            </section>
            <section v-if="adminPath">
              <strong>{{ uiText('Administration', 'Administration') }}</strong>
              <a :href="adminPath" data-router-link>Admin</a>
            </section>
          </div>
        </details>
      </nav>
    </header>

    <div
      class="game-body-shell"
      :class="{ 'without-world': !world, 'admin-workspace': adminWorkspace }"
    >
      <aside
        v-if="world"
        id="context-side-navigation"
        class="classic-side-nav contextual-side-nav"
        :aria-label="uiText('Kontextnavigation', 'Context navigation')"
      >
        <div class="side-nav-context-head">
          <div class="side-nav-context-copy">
            <strong>{{ sidebarSection.label }}</strong>
            <small>{{ sidebarContextName }}</small>
          </div>
          <button
            type="button"
            class="side-nav-collapse"
            :aria-label="
              sidebarCompact
                ? uiText('Sidebar ausklappen', 'Expand sidebar')
                : uiText('Sidebar einklappen', 'Collapse sidebar')
            "
            :title="
              sidebarCompact
                ? uiText('Sidebar ausklappen', 'Expand sidebar')
                : uiText('Sidebar einklappen', 'Collapse sidebar')
            "
            :aria-expanded="!sidebarCompact"
            @click="toggleSidebarDensity"
          >
            <span aria-hidden="true">{{ sidebarCompact ? '›' : '‹' }}</span>
          </button>
        </div>

        <section class="side-nav-group side-nav-context-group">
          <a
            v-for="item in sidebarSection.items"
            :key="item.path"
            :href="item.path"
            data-router-link
            :class="{ active: active(item.path) }"
            :title="sidebarCompact ? item.label : undefined"
          >
            <UiIcon :name="item.icon" />
            <span>{{ item.label }}</span>
          </a>
        </section>
      </aside>

      <main id="main-content" tabindex="-1"><slot /></main>
    </div>

    <footer v-if="world" class="game-footer">
      <span>Craft Imperium</span>
      <span class="footer-links"
        >Support · Spielregeln · Impressum · Datenschutz</span
      >
      <span>Browser-Strategiespiel</span>
    </footer>

    <nav
      v-if="world"
      class="mobile-nav"
      :aria-label="uiText('Hauptnavigation', 'Main navigation')"
    >
      <a
        v-for="item in primaryMobile"
        :key="item.path"
        :href="item.path"
        data-router-link
        :class="{ active: active(item.path) }"
        ><UiIcon :name="item.icon" /><small>{{ item.label }}</small></a
      >
      <details class="mobile-more">
        <summary><UiIcon name="more" /><small>Mehr</small></summary>
        <div class="mobile-more-panel">
          <a
            v-for="item in secondaryMobile"
            :key="item.path"
            :href="item.path"
            data-router-link
            >{{ item.label }}</a
          >
          <a href="/security" data-router-link>Sicherheitscenter</a>
          <a href="/settings" data-router-link>{{
            uiText('Einstellungen', 'Settings')
          }}</a>
          <a v-if="adminPath" :href="adminPath" data-router-link>Admin</a>
        </div>
      </details>
    </nav>
  </div>
</template>
